import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LOCALES, locale, setLocale, t, LocaleController, type Locale } from '../lib/i18n';
import { icon } from '../lib/icons';

/** How long the button keeps its old label after the language changes. */
const LABEL_HOLD_MS = 3000;

/**
 * <lang-picker> — switch the interface language.
 *
 * The button names the language you would GET, not the one you are reading:
 * reading English, it offers বাংলা. A control labelled with the state you are
 * already in tells you nothing, and with two languages a menu to pick from is
 * a list of one real choice.
 *
 * Each name is written in its own script, because somebody looking for their
 * language is scanning for its shapes — "Bangla" spelled in Latin is no help to
 * a reader who wants out of English.
 *
 * The page switches the instant it is pressed; the button's own label is held
 * for a moment afterwards. Flipping the thing under the finger at the same time
 * as everything else makes it briefly unclear what was just pressed.
 */
@customElement('lang-picker')
export class LangPicker extends LitElement {
  // Re-renders this element when the interface language changes.
  readonly i18n = new LocaleController(this);

  /** The label held over from before the switch, or null when settled. */
  @state() private held: string | null = null;
  private timer?: ReturnType<typeof setTimeout>;

  static styles = css`
    :host { display:inline-flex; }
    .btn { display:inline-flex; align-items:center; gap:6px; cursor:pointer; min-height:38px;
      background:transparent; border:1px solid var(--app-outline,#2a2f3a); border-radius:999px;
      color:var(--app-text); font:inherit; font-size:12.5px; font-weight:600; padding:6px 13px;
      transition:border-color var(--dur-2,180ms), background var(--dur-2,180ms); }
    .btn:hover { border-color:var(--app-primary,#1b7a52); }
    .btn svg { width:16px; height:16px; flex:none; color:var(--app-primary,#34d399); }
    /* The label crossfades when it finally does change, so the delay reads as
       deliberate rather than as lag. */
    .lbl { transition:opacity var(--dur-2,180ms); }
  `;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.timer);
  }

  /** The language this button switches TO. With two languages that is simply
   *  the other one; with more it would be the next in the list. */
  private get target(): Locale {
    const i = LOCALES.findIndex((l) => l.code === locale());
    return LOCALES[(i + 1) % LOCALES.length].code;
  }

  private nameOf(code: Locale) {
    return (LOCALES.find((l) => l.code === code) ?? LOCALES[0]).native;
  }

  render() {
    const settled = this.nameOf(this.target);
    return html`
      <button class="btn" lang=${this.held ? undefined : this.target}
        aria-label=${t('lang.title')} @click=${this.toggle}>
        ${icon('language')}<span class="lbl">${this.held ?? settled}</span>
      </button>`;
  }

  private toggle = () => {
    // Hold what the button says now, switch the page, then let the label catch
    // up. Without the hold, the control relabels itself under the finger at the
    // same instant the whole page changes.
    this.held = this.nameOf(this.target);
    setLocale(this.target);
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.held = null; }, LABEL_HOLD_MS);
  };
}

declare global {
  interface HTMLElementTagNameMap { 'lang-picker': LangPicker; }
}
